import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { href: "#hotel", label: "Das Hotel" },
  { href: "#zimmer", label: "Zimmer & Preise" },
  { href: "#region", label: "Region" },
  { href: "#faq", label: "FAQ" },
  { href: "#kontakt", label: "Kontakt" },
];

export const scrollToId = (id) => {
  const el = document.querySelector(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(href), 350);
    } else scrollToId(href);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 border-b ${
        scrolled
          ? "bg-sand/85 backdrop-blur-md border-forest/10 shadow-[0_8px_30px_rgba(27,59,43,0.08)]"
          : "bg-transparent border-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img
            src="/logo.png"
            alt="Hotel Wildpferd Dülmen"
            className={`h-12 w-auto drop-shadow-sm transition-[transform,background-color,padding] duration-500 group-hover:scale-[1.04] ${
              scrolled ? "" : "rounded-xl bg-sand/85 backdrop-blur-sm px-2.5 py-1 shadow-lg shadow-forest-dark/25"
            }`}
          />
          <span className={`hidden sm:inline font-mono text-[10px] uppercase tracking-[0.3em] transition-colors duration-500 ${scrolled ? "text-gold" : "text-gold"}`}>
            Dülmen
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              onClick={() => go(l.href)}
              className={`text-sm font-medium transition-colors duration-300 relative after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-gold after:transition-[width] after:duration-300 hover:after:w-full ${
                scrolled ? "text-ink/70 hover:text-forest" : "text-sand/85 hover:text-white"
              }`}
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/buchen"
            data-testid="nav-book-button"
            className="ml-2 rounded-full bg-forest text-sand px-6 py-2.5 text-sm font-semibold hover:bg-forest-dark hover:shadow-lg hover:shadow-forest/20 transition-[background-color,box-shadow] duration-300"
          >
            Zimmer buchen
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-forest p-2"
          onClick={() => setOpen(!open)}
          aria-label="Menü"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-sand/95 backdrop-blur-md border-t border-forest/10 px-6 py-6 flex flex-col gap-4">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-mobile-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              onClick={() => go(l.href)}
              className="text-left text-base font-medium text-ink/80"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/buchen"
            data-testid="nav-mobile-book-button"
            onClick={() => setOpen(false)}
            className="rounded-full bg-forest text-sand px-6 py-3 text-sm font-semibold text-center"
          >
            Zimmer buchen
          </Link>
        </div>
      )}
    </motion.header>
  );
}
